import classNames from 'classnames';
import { Card } from 'components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  getSimplePaginationItems,
} from 'components/ui/pagination';
import { useAuth } from 'hooks/useAuth';
import { ApiData } from 'interfaces/api';
import { GeoMap } from 'interfaces/geo-map';
import { PageCartoForm } from 'interfaces/page-carto';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import { GeoListItem } from './GeoListItem';
import { useService } from 'hooks/useService';
import { ContentType } from 'services/abstract';
const GEO_MAP_TABS = [
  {
    id: 'my',
    title: 'Mes cartes',
  },
  {
    id: 'all',
    title: 'Toutes les cartes',
  },
];

export const GeoMapList = () => {
  const { setValue } = useFormContext<PageCartoForm>();
  const [page, setPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState(GEO_MAP_TABS[0]?.id ?? 'my');
  const [selectedGeoMap, setSelectedGeoMap] = useState({} as ApiData<GeoMap>);
  const { user } = useAuth();
  const geomapService = useService(ContentType.GEO_MAPS);

  const { data: response } = useQuery({
    queryKey: ['latest-geo-carto', page, selectedTab],
    queryFn: async () => {
      return await geomapService.getGeoMaps(
        page,
        9,
        selectedTab === 'my' && user ? user.id : undefined
      );
    },
  });

  useEffect(() => {
    if (selectedGeoMap.id !== undefined) {
      setValue('mapId', selectedGeoMap.id);
    }
  }, [selectedGeoMap, setValue]);

  const handleSelect = (geoMap: ApiData<GeoMap>) => {
    setSelectedGeoMap(geoMap);
  };

  const totalPages = response?.meta.pagination.pageCount ?? 0;
  const paginationItems = getSimplePaginationItems(page, totalPages);
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;

  return (
    <Tabs
      value={selectedTab}
      onValueChange={setSelectedTab}
      className="flex-column"
    >
      <TabsList
        data-testid="map-choice"
        className="flex bg-blue-600 rounded-t-lg p-2"
      >
        {GEO_MAP_TABS.map((table) => (
          <TabsTrigger
            key={table.id}
            value={table.id}
            className={classNames(
              'rounded-t-lg p-4 text-sm font-medium leading-5 text-blue-100 hover:bg-white/10 hover:text-white cursor-pointer',
              'ring-blue ring-opacity-60 ring-offset-1 ring-offset-blue-400 focus:outline-none focus:ring-1',
              'data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow'
            )}
          >
            {table.title}
          </TabsTrigger>
        ))}
      </TabsList>
      {GEO_MAP_TABS.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className="rounded-b-lg border border-blue-700 p-5"
        >
          <div className="grid grid-cols-3 gap-y-10 gap-x-6">
            {response?.data.map((geoMap) => {
              return (
                <Card
                  key={geoMap.id}
                  id="mapId"
                  onClick={() => {
                    handleSelect(geoMap);
                  }}
                  role="button"
                  className={classNames(
                    'flex h-full flex-col justify-center gap-4 cursor-pointer transition-colors hover:bg-gray-100',
                    geoMap.id === selectedGeoMap?.id &&
                      'border-blue-700 bg-blue-50'
                  )}
                >
                  <GeoListItem {...geoMap} />
                </Card>
              );
            })}
          </div>
          <div className="flex flex-row mt-5 justify-center">
            <Pagination>
              <PaginationContent
                data-testid="pagination"
                className="flex-wrap justify-center"
              >
                {hasPrevious && (
                  <PaginationItem>
                    <PaginationPrevious
                      data-testid="pagination-previous"
                      onClick={() => setPage(page - 1)}
                    />
                  </PaginationItem>
                )}
                {paginationItems.map((pageNumber) => (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      isActive={pageNumber === page}
                      disabled={pageNumber === page}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                {hasNext && (
                  <PaginationItem>
                    <PaginationNext
                      data-testid="pagination-next"
                      onClick={() => setPage(page + 1)}
                    />
                  </PaginationItem>
                )}
              </PaginationContent>
            </Pagination>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};
